// LiveDot — pulsing status dot for connected-service indicators (Live status,
// AI, monitoring). Token-styled (--score-*/--ps-pill-*-tx) so it flips in dark.
// The pulse keyframe is self-contained (scoped `ps-pulse-dot`) and collapses
// under prefers-reduced-motion. `live`/`degraded` pulse; `down` is solid.
import * as React from 'react';

type LiveStatus = 'live' | 'degraded' | 'down';

export interface LiveDotProps {
  /** Service health. @default "live" */
  status?: LiveStatus;
  /** Text label next to the dot. Omit for a bare dot; "" uses the default label. */
  label?: string;
  /** Dot diameter in px. @default 8 */
  size?: number;
  /** Force pulse on/off. Defaults to pulse unless status is "down". */
  animate?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const STATUS_CONFIG: Record<
  LiveStatus,
  { color: string; tx: string; defaultLabel: string }
> = {
  live: { color: 'var(--score-good)', tx: 'var(--ps-pill-green-tx)', defaultLabel: 'Live' },
  degraded: { color: 'var(--score-warning)', tx: 'var(--ps-pill-amber-tx)', defaultLabel: 'Degraded' },
  down: { color: 'var(--score-danger)', tx: 'var(--ps-pill-red-tx)', defaultLabel: 'Offline' },
};

// Scoped keyframe + reduced-motion collapse — self-contained so this doesn't
// depend on any app-level globals.css defining a pulse animation.
const KEYFRAMES =
  '@keyframes ps-pulse-dot{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.55;transform:scale(.82)}}' +
  '.ps-live-dot--pulse{animation:ps-pulse-dot 1.8s ease-in-out infinite}' +
  '@media (prefers-reduced-motion: reduce){.ps-live-dot--pulse{animation:none}}';

export function LiveDot({
  status = 'live',
  label,
  size = 8,
  animate,
  className = '',
  style,
}: LiveDotProps) {
  const cfg = STATUS_CONFIG[status];
  const shouldAnimate = animate ?? status !== 'down';
  const displayLabel = label === undefined ? null : label || cfg.defaultLabel;

  const dot = (
    <span
      role="status"
      aria-label={`${status} status`}
      title={cfg.defaultLabel}
      className={shouldAnimate ? 'ps-live-dot--pulse' : undefined}
      style={{
        display: 'inline-block',
        flexShrink: 0,
        borderRadius: 'var(--radius-pill)',
        width: size,
        height: size,
        backgroundColor: cfg.color,
      }}
    />
  );

  const keyframes = <style>{KEYFRAMES}</style>;

  if (displayLabel === null) {
    return (
      <span className={className} style={style}>
        {keyframes}
        {dot}
      </span>
    );
  }

  return (
    <span
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-sans)', ...style }}
    >
      {keyframes}
      {dot}
      <span style={{ fontSize: 14, fontWeight: 500, color: cfg.tx }}>{displayLabel}</span>
    </span>
  );
}

export default LiveDot;
